import React, { useState, useEffect, useContext } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";
import {
  useCreateDatasetMapperData,
  useDeleteDatasetMapperData,
  useGetDatasetMappersData,
  useUpdateDatasetMapperData,
} from "api/api-hooks/dataset-mapper-hooks";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import MapperMappings from "./MapperMappings";
import CreateEditDatasetMapper from "./CreateEditDatasetMapper";

const ManageDatasetMappers = () => {
  const [
    createDatasetMappersDialogVisibility,
    setCreateDatasetMappersDialogVisibility,
  ] = useState<boolean>(false);
  const [
    editDatasetMapperDialogVisibility,
    setEditDatasetMapperDialogVisibility,
  ] = useState<boolean>(false);
  const [mapperMappingDialogVisibility, setMapperMappingDialogVisibility] =
    useState<boolean>(false);
  const [selectedMapper, setSelectedMapper] =
    useState<DatasetStringValueMapperDTO>();
  const [mappers, setMappers] = useState<DatasetStringValueMapperDTO[]>([]);

  const {
    data: currentMappers,
    isLoading,
    isError,
    refetch: refetchMappers,
  } = useGetDatasetMappersData();

  const createMutation = useCreateDatasetMapperData();
  const updateMutation = useUpdateDatasetMapperData();
  const deleteMutation = useDeleteDatasetMapperData();

  useEffect(() => {
    if (!isLoading && !isError) {
      setMappers([...currentMappers]);
    }
  }, [currentMappers, isLoading, isError]);

  useEffect(() => {
    refetchMappers();
  }, [refetchMappers]);

  const handleDeleteMapper = async (mapper: DatasetStringValueMapperDTO) => {
    const newMappersArray = mappers.filter((m) => m.id !== mapper.id);
    await setMappers(newMappersArray);
    deleteMutation.mutate(mapper);
  };

  const handleRowEditClick = (mapper: DatasetStringValueMapperDTO) => {
    setSelectedMapper(mapper);
    setEditDatasetMapperDialogVisibility(true);
  };

  const handleEditDialogSave = async (
    updatedMapper: DatasetStringValueMapperDTO
  ) => {
    if (updatedMapper) {
      for (let i = 0; i < mappers.length; i++) {
        if (mappers[i].id === updatedMapper.id) {
          mappers[i] = updatedMapper;
          await updateMutation.mutate(updatedMapper);
          break;
        }
      }
    }
    setEditDatasetMapperDialogVisibility(false);
  };

  const createDatasetMapper = async (
    newMapper: DatasetStringValueMapperDTO
  ) => {
    if (newMapper) {
      await setMappers([...mappers, newMapper]);
      createMutation.mutate(newMapper);
    }
    setCreateDatasetMappersDialogVisibility(false);
  };
  
  return (
    <div>
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching data</div>}
      {!isLoading && !isError && (
        <div>
          <DataTable
            value={mappers}
            editMode="row"
            dataKey="id"
            scrollHeight="300px"
            responsiveLayout="scroll"
            size="small"
          >
            <Column field="title" header="Title" />
            <Column
              header="Global"
              body={(rowData) => (rowData.global ? "Global" : "Not Global")}
            />
            <Column
              header="Mappings"
              body={(rowData: DatasetStringValueMapperDTO) => {
                const handleMappingClick = () => {
                  setSelectedMapper(rowData);
                  setMapperMappingDialogVisibility(true);
                };
                return (
                  <div>
                    <div>
                      {Object.keys(rowData.mappings).length === 0 ? (
                        <h3>No mappings</h3>
                      ) : (
                        <ul>
                          {Object.entries(rowData.mappings)
                            .slice(0, 3)
                            .map(([key, value]) => (
                              <li key={key}>
                                {key}: {value}
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                    {Object.keys(rowData.mappings).length > 3 && (
                      <Button
                        type="button"
                        label="See all mappings"
                        className="p-button-text p-0"
                        onClick={handleMappingClick}
                      />
                    )}
                  </div>
                );
              }}
            />

            <Column
              body={(mapperData) => (
                <React.Fragment>
                  {mapperData.global && (
                    <PermissionGuard permissions="ADMIN">
                      <Button
                        type="button"
                        icon="pi pi-pencil"
                        className="p-button-rounded p-button-info p-button-text p-ml-2"
                        onClick={() => handleRowEditClick(mapperData)}
                      />
                    </PermissionGuard>
                  )}
                  {!mapperData.global && (
                    <Button
                      type="button"
                      icon="pi pi-pencil"
                      className="p-button-rounded p-button-info p-button-text p-ml-2"
                      onClick={() => handleRowEditClick(mapperData)}
                    />
                  )}
                </React.Fragment>
              )}
              style={{ width: "3%" }}
            />
            <Column
              body={(mapperData) => (
                <React.Fragment>
                  {mapperData.global && (
                    <PermissionGuard permissions="ADMIN">
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
                        onClick={() => handleDeleteMapper(mapperData)}
                      />
                    </PermissionGuard>
                  )}
                  {!mapperData.global && (
                    <Button
                      type="button"
                      icon="pi pi-trash"
                      className="const-del-btn  p-button-rounded p-button-text p-button-danger"
                      onClick={() => handleDeleteMapper(mapperData)}
                    />
                  )}
                </React.Fragment>
              )}
              style={{ width: "3%" }}
            />
          </DataTable>
        </div>
      )}
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "400px" }}
        header="Mappings"
        visible={mapperMappingDialogVisibility}
        onHide={() => setMapperMappingDialogVisibility(false)}
        resizable={true}
      >
        {selectedMapper && <MapperMappings selectedMapper={selectedMapper} />}
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header="Create Mapper"
        visible={createDatasetMappersDialogVisibility}
        onHide={() => setCreateDatasetMappersDialogVisibility(false)}
        resizable={true}
      >
        <CreateEditDatasetMapper
          onCancel={() => setCreateDatasetMappersDialogVisibility(false)}
          onSave={createDatasetMapper}
        />
      </Dialog>
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "600px" }}
        header="Edit Mapper"
        visible={editDatasetMapperDialogVisibility}
        onHide={() => setEditDatasetMapperDialogVisibility(false)}
        resizable={true}
      >
        {selectedMapper && (
          <CreateEditDatasetMapper
            mapperData={selectedMapper}
            onSave={handleEditDialogSave}
            onCancel={() => setEditDatasetMapperDialogVisibility(false)}
          />
        )}
      </Dialog>
      <Button
        className="p-button-success h-10 w-1.5"
        label="Create new"
        onClick={() => setCreateDatasetMappersDialogVisibility(true)}
      />
    </div>
  );
};

export default ManageDatasetMappers